import { Button, Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MyTaskExecutionRow } from "shared/types";

interface UseMyTaskExecutionsColumnsProps {
  /**
   * When provided, task rows get a "Complete task" action mirroring the
   * "Complete my tasks" page. Omit it for a read-only tree.
   */
  onCompleteTask?: (taskExecutionId: number) => void;
}

/**
 * Columns for the "My tasks" summary tree (see `MaintenanceWidget`). Section
 * rows show `total_task_executions`; task rows show the execution details.
 */
export const useMyTaskExecutionsColumns = ({
  onCompleteTask,
}: UseMyTaskExecutionsColumnsProps = {}): ColumnsType<MyTaskExecutionRow> => {
  const { t } = useTranslation();

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const columns: ColumnsType<MyTaskExecutionRow> = [
    {
      title: t("taskHub.myTasks.taskSection") || "Task / Section",
      dataIndex: "name",
      key: "name",
      render: (_, row) =>
        row.rowType === "section" ? (
          <span className="font-semibold">{row.name}</span>
        ) : (
          row.name
        ),
    },
    {
      title:
        t("taskHub.myTasks.totalTaskExecutions") || "Total task executions",
      key: "total",
      width: 170,
      align: "center",
      render: (_, row) =>
        row.rowType === "section" ? (
          <span className="font-semibold">{row.total}</span>
        ) : null,
    },
    {
      title: t("taskHub.myTasks.expected") || "Expected",
      key: "expected",
      width: 140,
      render: (_, row) =>
        row.rowType === "task" && row.expected
          ? dayjs(row.expected).format("YYYY-MM-DD HH:mm")
          : null,
    },
    {
      title: t("taskHub.myTasks.priority") || "Priority",
      key: "priority",
      width: 90,
      align: "center",
      render: (_, row) => (row.rowType === "task" ? row.priority : null),
    },
    {
      title: t("taskHub.myTasks.status") || "Status",
      key: "status",
      width: 110,
      render: (_, row) =>
        row.rowType === "task" ? <Tag>{row.status ?? "-"}</Tag> : null,
    },
    {
      title: t("taskHub.myTasks.message") || "Message",
      key: "message",
      width: 160,
      render: (_, row) => (row.rowType === "task" ? row.message ?? "-" : null),
    },
  ];

  if (onCompleteTask) {
    columns.push({
      title: t("taskHub.myTasks.actions") || "Actions",
      key: "actions",
      width: 160,
      align: "center",
      render: (_, row) =>
        row.rowType === "task" && row.id != null ? (
          <Button
            type="primary"
            size="small"
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
            onClick={() => onCompleteTask(row.id as number)}
          >
            {t("taskHub.myTasks.completeTask") || "Complete task"}
          </Button>
        ) : null,
    });
  }

  return columns;
};
